/* Two-part stacked columns, optionally with a line on a second axis.
 *
 * Used for the unit split (Unit 1 under Unit 2) and for power (EB under
 * solar, with units-per-kg as the line). Stacking is right for both because
 * the total is the number that matters and the split is the detail.
 */

import { GridLines, Plot } from './Plot'
import { bandValues, niceMax, polyline, slotCentre, slotWidth, yScale } from '../../lib/scale'

export function Stacked({
  height = 200, lower, upper, colors, labelY, yTitle, xLabels,
  line, lineLabelY, lineTitle, tooltip,
}: {
  height?: number
  lower: number[]
  upper: number[]
  colors: [string, string]
  labelY: (v: number) => string
  yTitle: string
  xLabels?: React.ReactNode
  line?: (number | null)[]
  lineLabelY?: (v: number) => string
  lineTitle?: string
  /** Hover readout for column `i`. */
  tooltip?: (i: number) => React.ReactNode
}) {
  const n = lower.length
  const max = niceMax(Math.max(...lower.map((v, i) => v + upper[i]), 1))
  const y = yScale(0, max, height)
  const slot = slotWidth(n)
  const w = slot * 0.62

  const lineVals = (line ?? []).filter((v): v is number => v != null && v > 0)
  const lineMax = niceMax(Math.max(...(lineVals.length ? lineVals : [1])))
  const yLine = yScale(0, lineMax, height)

  // A gap in the line (a day with no reading) breaks it into segments rather
  // than being drawn through at zero, which would read as a real collapse.
  const segments: [number, number][][] = []
  if (line) {
    let run: [number, number][] = []
    line.forEach((v, i) => {
      if (v == null || v <= 0) {
        if (run.length > 1) segments.push(run)
        run = []
      } else {
        run.push([slotCentre(i, n), yLine(v)])
      }
    })
    if (run.length > 1) segments.push(run)
  }

  return (
    <Plot
      height={height}
      yTitle={yTitle}
      yLabels={bandValues(0, max, 3).map(labelY)}
      yLabelsRight={line && lineLabelY ? bandValues(0, lineMax, 3).map(lineLabelY) : undefined}
      yTitleRight={lineTitle}
      xLabels={xLabels}
      bands={n}
      tooltip={tooltip}
    >
      <GridLines ys={bandValues(0, max, 3).map(y)} />

      {lower.map((lo, i) => {
        const x = slot * i + (slot - w) / 2
        const h1 = (lo / max) * height
        const h2 = (upper[i] / max) * height
        return (
          <g key={i}>
            <rect x={x} y={height - h1} width={w} height={h1} fill={colors[0]} />
            <rect x={x} y={height - h1 - h2} width={w} height={h2} fill={colors[1]} />
          </g>
        )
      })}

      {segments.map((seg, i) => (
        <polyline key={i} points={polyline(seg)} fill="none"
          stroke="var(--color-accent-900)" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
      ))}
    </Plot>
  )
}

/** A single trend line — the yarn-stock sparkline. */
export function TrendLine({ values, height = 130, labelY, yTitle, xLabels, tooltip }: {
  values: number[]
  height?: number
  labelY: (v: number) => string
  yTitle: string
  xLabels?: React.ReactNode
  /** Hover readout for point `i`. */
  tooltip?: (i: number) => React.ReactNode
}) {
  const n = values.length
  const max = niceMax(Math.max(...values, 1))
  const y = yScale(0, max, height)
  const pts = values.map((v, i) => [slotCentre(i, n), y(v)] as [number, number])

  return (
    <Plot
      height={height}
      yTitle={yTitle}
      yLabels={bandValues(0, max, 3).map(labelY)}
      xLabels={xLabels}
      bands={n}
      tooltip={tooltip}
    >
      <GridLines ys={bandValues(0, max, 3).map(y)} />
      <polyline points={polyline(pts)} fill="none" stroke="var(--color-accent-900)"
        strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
    </Plot>
  )
}
